import React, { useCallback, useEffect, useRef, useState } from "react";

const DISPLAY_INK = "#eef2f6";
const DISPLAY_PAPER = "#070a0f";
const EXPORT_INK = "#151a21";
const EXPORT_PAPER = "#f7f8fa";
const MAX_DPR = 2;

function metrics(canvas) {
  const rect = canvas.getBoundingClientRect();
  return { rect, width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
}

function pointFor(canvas, event) {
  const { rect, width, height } = metrics(canvas);
  return {
    x: Math.max(0, Math.min(1, (event.clientX - rect.left) / width)),
    y: Math.max(0, Math.min(1, (event.clientY - rect.top) / height)),
  };
}

function renderStrokes(ctx, strokes, width, height, { paper, ink }) {
  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = Math.max(3, Math.min(8, width * 0.0075));
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  strokes.forEach((stroke) => {
    if (!stroke.length) return;
    if (stroke.length === 1) {
      const p = stroke[0];
      ctx.beginPath();
      ctx.arc(p.x * width, p.y * height, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(stroke[0].x * width, stroke[0].y * height);
    for (let i = 1; i < stroke.length; i += 1) {
      ctx.lineTo(stroke[i].x * width, stroke[i].y * height);
    }
    ctx.stroke();
  });
}

function paint(canvas, strokes) {
  if (!canvas) return;
  const { width, height } = metrics(canvas);
  const dpr = Math.min(MAX_DPR, Math.max(1, window.devicePixelRatio || 1));
  const pixelWidth = Math.max(1, Math.round(width * dpr));
  const pixelHeight = Math.max(1, Math.round(height * dpr));
  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
  }
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  renderStrokes(ctx, strokes, width, height, { paper: DISPLAY_PAPER, ink: DISPLAY_INK });
}

function exportPng(strokes, sourceCanvas) {
  return new Promise((resolve, reject) => {
    const { width, height } = metrics(sourceCanvas);
    const longest = Math.max(width, height);
    const scale = Math.min(1, 1400 / longest);
    const out = document.createElement("canvas");
    out.width = Math.max(512, Math.round(width * scale));
    out.height = Math.max(512, Math.round(height * scale));
    const ctx = out.getContext("2d", { alpha: false });
    if (!ctx) return reject(new Error("Could not prepare your sketch."));
    renderStrokes(ctx, strokes, out.width, out.height, { paper: EXPORT_PAPER, ink: EXPORT_INK });
    out.toBlob((blob) => {
      if (!blob) return reject(new Error("Could not prepare your sketch."));
      resolve(new File([blob], `todoar-sketch-${Date.now()}.png`, { type: "image/png" }));
    }, "image/png");
  });
}

export default function GatewaySketchPad({ onCreate, busy = false }) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const activeRef = useRef(null);
  const frameRef = useRef(null);
  const [strokeCount, setStrokeCount] = useState(0);
  const [localError, setLocalError] = useState("");

  const redraw = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      paint(canvasRef.current, strokesRef.current);
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const resize = () => paint(canvas, strokesRef.current);
    resize();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(resize);
      observer.observe(canvas);
    } else {
      window.addEventListener("resize", resize);
    }
    return () => {
      observer?.disconnect();
      if (!observer) window.removeEventListener("resize", resize);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const begin = (event) => {
    if (busy || (event.pointerType === "mouse" && event.button !== 0)) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(event.pointerId);
    const stroke = [pointFor(canvas, event)];
    strokesRef.current.push(stroke);
    activeRef.current = stroke;
    setStrokeCount(strokesRef.current.length);
    setLocalError("");
    redraw();
  };

  const move = (event) => {
    if (busy || !activeRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = pointFor(canvas, event);
    const stroke = activeRef.current;
    const previous = stroke[stroke.length - 1];
    if (Math.hypot(point.x - previous.x, point.y - previous.y) < 0.0014) return;
    stroke.push(point);
    redraw();
  };

  const end = (event) => {
    if (!activeRef.current) return;
    event.preventDefault();
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
    activeRef.current = null;
    redraw();
  };

  const undo = () => {
    if (busy || !strokesRef.current.length) return;
    strokesRef.current.pop();
    activeRef.current = null;
    setStrokeCount(strokesRef.current.length);
    paint(canvasRef.current, strokesRef.current);
  };

  const clear = () => {
    if (busy) return;
    strokesRef.current = [];
    activeRef.current = null;
    setStrokeCount(0);
    setLocalError("");
    paint(canvasRef.current, []);
  };

  const create = async () => {
    if (busy || !strokesRef.current.length || !canvasRef.current) return;
    setLocalError("");
    try {
      await onCreate(await exportPng(strokesRef.current, canvasRef.current));
    } catch (error) {
      setLocalError(error?.message || "Could not use this sketch.");
    }
  };

  return (
    <div className="gateway-sketch-shell">
      <canvas
        ref={canvasRef}
        className="gateway-sketch-canvas"
        aria-label="Draw your idea with a finger, mouse or pen"
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div className="gateway-sketch-tools" aria-label="Sketch controls">
        <button type="button" onClick={undo} disabled={busy || strokeCount === 0}>Undo</button>
        <button type="button" onClick={clear} disabled={busy || strokeCount === 0}>Clear</button>
      </div>
      {strokeCount === 0 ? <p className="gateway-sketch-hint">Use your finger, pen or mouse.</p> : null}
      <button type="button" className="gateway-make3d" onClick={create} disabled={busy || strokeCount === 0}>
        {busy ? "Forming…" : "Make it 3D"}
      </button>
      {localError ? <p className="gateway-sketch-error" role="alert">{localError}</p> : null}
    </div>
  );
}
